import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { predictorApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, Target, Loader2, Trophy, TrendingUp,
  AlertTriangle, Flame, Building2, History, Crown, ChevronRight,
} from 'lucide-react';

// Phase 36 — Rank & College Predictor
export default function RankPredictor() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [examId, setExamId] = useState('jee_main');
  const [score, setScore] = useState('');
  const [percentile, setPercentile] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await predictorApi.history();
      setHistory(r.history || []);
    } catch { /* empty */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const submit = async () => {
    if (!score && !percentile) {
      toast.error('Enter a score or percentile');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await predictorApi.predict({
        examId,
        score: examId === 'neet_ug' ? score : score,
        percentile: examId !== 'neet_ug' ? percentile : null,
      });
      setResult(r);
      loadHistory();
    } catch (e) {
      toast.error(e.message || 'Prediction failed');
    }
    setLoading(false);
  };

  const scoreMax = examId === 'neet_ug' ? 720 : 300;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="rp-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-500" />Rank & College Predictor
              </h1>
              <p className="text-xs text-muted-foreground">Estimate your All-India Rank and see where you'd fit, based on JoSAA / counselling cutoffs.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Input card */}
        <div className="p-6 rounded-2xl border border-border bg-card" data-testid="rp-input-card">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div className="sm:col-span-1">
              <label className="text-xs font-semibold text-muted-foreground">Exam</label>
              <Select value={examId} onValueChange={(v) => { setExamId(v); setResult(null); }}>
                <SelectTrigger className="mt-1" data-testid="rp-exam-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="jee_main">JEE Main</SelectItem>
                  <SelectItem value="jee_advanced">JEE Advanced</SelectItem>
                  <SelectItem value="neet_ug">NEET UG</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">
                {examId === 'neet_ug' ? 'Score (/720)' : 'Score (/300)'}
              </label>
              <Input
                type="number" min="0" max={scoreMax}
                value={score} onChange={(e) => setScore(e.target.value)}
                placeholder={`0 - ${scoreMax}`}
                className="mt-1"
                data-testid="rp-score-input"
              />
            </div>
            {examId !== 'neet_ug' && (
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Percentile (optional)</label>
                <Input
                  type="number" step="0.001" min="0" max="100"
                  value={percentile} onChange={(e) => setPercentile(e.target.value)}
                  placeholder="0 - 100"
                  className="mt-1"
                  data-testid="rp-percentile-input"
                />
              </div>
            )}
            <Button
              onClick={submit}
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="rp-predict-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
              Predict Rank
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            {examId === 'neet_ug'
              ? 'NEET UG: enter your raw score out of 720.'
              : 'JEE Main: enter either your score (out of 300) OR your official percentile.'}
          </p>
        </div>

        {/* Result */}
        {result && (
          <div className="space-y-5" data-testid="rp-result">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard
                color="amber" icon={Crown}
                label="Predicted All-India Rank"
                value={result.predicted_rank?.toLocaleString()}
                hint={`Range: ${result.bracket.low.toLocaleString()} - ${result.bracket.high.toLocaleString()}`}
                testId="rp-stat-rank"
              />
              <StatCard
                color="emerald" icon={TrendingUp}
                label="Predicted Percentile"
                value={`${result.predicted_percentile}%`}
                hint={`${result.exam_name}`}
                testId="rp-stat-percentile"
              />
              <StatCard
                color="indigo" icon={Building2}
                label="Colleges Shortlisted"
                value={result.reach_colleges.length + result.match_colleges.length + result.safety_colleges.length}
                hint="Reach + Match + Safety"
                testId="rp-stat-colleges"
              />
            </div>

            <Tier title="Reach" subtitle="Stretch goals — closing ranks well below yours" icon={Flame} color="rose"
              colleges={result.reach_colleges} testId="rp-reach" emptyMsg="No reach colleges at this rank — aim higher next time!" />
            <Tier title="Match" subtitle="Your sweet spot — likely realistic admits" icon={Trophy} color="emerald"
              colleges={result.match_colleges} testId="rp-match" emptyMsg="No match colleges — widen the search exam." />
            <Tier title="Safety" subtitle="High confidence — strong fallbacks" icon={Target} color="sky"
              colleges={result.safety_colleges} testId="rp-safety" emptyMsg="No explicit safety in bank — consider state-quota options." />

            <div className="p-4 rounded-2xl border border-amber-200 dark:border-amber-900 bg-amber-50/40 dark:bg-amber-950/10 text-xs text-amber-900 dark:text-amber-300 flex gap-2" data-testid="rp-disclaimer">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{result.disclaimer}</span>
            </div>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="p-5 rounded-2xl border border-border bg-card" data-testid="rp-history">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-indigo-500" />
              <h3 className="font-bold">Your recent predictions</h3>
            </div>
            <div className="space-y-1.5">
              {history.slice(0, 5).map((h, i) => (
                <div key={h.history_id || i} className="flex items-center gap-3 p-2.5 rounded-lg border border-border text-xs" data-testid={`rp-history-${i}`}>
                  <Badge variant="outline" className="text-[10px]">{h.exam_id}</Badge>
                  {h.score != null && <span>Score: <b>{h.score}</b></span>}
                  <span>P: <b>{h.percentile}%</b></span>
                  <span className="ml-auto">AIR ~ <b>{h.rank?.toLocaleString()}</b></span>
                  <span className="text-muted-foreground">{new Date(h.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, hint, testId }) {
  const cls = {
    amber:   'bg-amber-100 dark:bg-amber-950/40 text-amber-500',
    emerald: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-500',
    indigo:  'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-500',
  }[color];
  return (
    <div className="p-5 rounded-2xl border border-border bg-card" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl ${cls} flex items-center justify-center mb-2`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className="text-3xl font-bold mt-0.5">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function Tier({ title, subtitle, icon: Icon, color, colleges, testId, emptyMsg }) {
  const cls = {
    rose:    'border-rose-200 dark:border-rose-900 text-rose-500',
    emerald: 'border-emerald-200 dark:border-emerald-900 text-emerald-500',
    sky:     'border-sky-200 dark:border-sky-900 text-sky-500',
  }[color];
  return (
    <div className={`p-5 rounded-2xl border ${cls} bg-card`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5" />
        <div>
          <h3 className="font-bold text-foreground">{title}</h3>
          <p className="text-[11px] text-muted-foreground">{subtitle}</p>
        </div>
        <Badge variant="outline" className="text-[10px] ml-auto">{colleges.length}</Badge>
      </div>
      {colleges.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4" data-testid={`${testId}-empty`}>{emptyMsg}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {colleges.map((c, i) => (
            <div key={i} className="p-3 rounded-xl border border-border bg-background/50 flex items-center gap-3" data-testid={`${testId}-college-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{c.name}</p>
                <p className="text-[11px] text-muted-foreground truncate">{c.branch} · {c.category}</p>
                {c.note && <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">{c.note}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-bold">{c.closing_rank.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">closing rank</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
